import React, { useState } from 'react'
import { Plus, Edit2, Trash2, ArrowUp, ArrowDown, Circle } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Button from '../../components/ui/Button'
import Modal from '../../components/Modal'
import Input from '../../components/ui/Input'
import TextArea from '../../components/ui/Textarea'
import { useToast } from '../../components/Toast'
import estadosOSService, { EstadoOSFormData } from '../../services/estadosOS'
import { EstadoOS } from '../../types'

const EstadosOSPage: React.FC = () => {
  const { success, error } = useToast()
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editingEstado, setEditingEstado] = useState<EstadoOS | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null)

  // Form state
  const [formData, setFormData] = useState<EstadoOSFormData>({
    nome: '',
    descricao: '',
    cor: '#3B82F6',
    ordem: 0,
    ativo: true,
    podeEditar: true,
    podeExcluir: true,
    notificarCliente: false,
    notificarTecnico: false,
    estadoInicial: false,
    estadoFinal: false
  })

  // Query para buscar estados
  const { data: estados = [], isLoading } = useQuery({
    queryKey: ['estados-os'],
    queryFn: () => estadosOSService.list()
  })

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: EstadoOSFormData) => estadosOSService.create(data),
    onSuccess: () => {
      success('Estado criado com sucesso')
      queryClient.invalidateQueries({ queryKey: ['estados-os'] })
      handleCloseModal()
    },
    onError: (err: any) => {
      error('Erro ao criar estado', err.message)
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<EstadoOSFormData> }) =>
      estadosOSService.update(id, data),
    onSuccess: () => {
      success('Estado atualizado com sucesso')
      queryClient.invalidateQueries({ queryKey: ['estados-os'] })
      handleCloseModal()
    },
    onError: (err: any) => {
      error('Erro ao atualizar estado', err.message)
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => estadosOSService.delete(id),
    onSuccess: () => {
      success('Estado excluído com sucesso')
      queryClient.invalidateQueries({ queryKey: ['estados-os'] })
      setShowDeleteConfirm(null)
    },
    onError: (err: any) => {
      error('Erro ao excluir estado', err.message)
    }
  })

  const reorderMutation = useMutation({
    mutationFn: (estados: { id: number; ordem: number }[]) =>
      estadosOSService.reorder(estados),
    onSuccess: () => {
      success('Ordem atualizada com sucesso')
      queryClient.invalidateQueries({ queryKey: ['estados-os'] })
    },
    onError: (err: any) => {
      error('Erro ao reordenar estados', err.message)
    }
  })

  const handleOpenModal = (estado?: EstadoOS) => {
    if (estado) {
      setEditingEstado(estado)
      setFormData({
        nome: estado.nome,
        descricao: estado.descricao || '',
        cor: estado.cor,
        ordem: estado.ordem,
        ativo: estado.ativo,
        podeEditar: estado.podeEditar,
        podeExcluir: estado.podeExcluir,
        notificarCliente: estado.notificarCliente,
        notificarTecnico: estado.notificarTecnico,
        estadoInicial: estado.estadoInicial || false,
        estadoFinal: estado.estadoFinal || false
      })
    } else {
      setEditingEstado(null)
      const maxOrdem = Math.max(...estados.map(e => e.ordem), 0)
      setFormData({
        nome: '',
        descricao: '',
        cor: '#3B82F6',
        ordem: maxOrdem + 1,
        ativo: true,
        podeEditar: true,
        podeExcluir: true,
        notificarCliente: false,
        notificarTecnico: false,
        estadoInicial: false,
        estadoFinal: false
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingEstado(null)
    setFormData({
      nome: '',
      descricao: '',
      cor: '#3B82F6',
      ordem: 0,
      ativo: true,
      podeEditar: true,
      podeExcluir: true,
      notificarCliente: false,
      notificarTecnico: false,
      estadoInicial: false,
      estadoFinal: false
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.nome) {
      error('Nome é obrigatório')
      return
    }

    if (editingEstado) {
      updateMutation.mutate({ id: editingEstado.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleMoveUp = (index: number) => {
    if (index === 0) return
    
    const newEstados = [...estados]
    const temp = newEstados[index].ordem
    newEstados[index].ordem = newEstados[index - 1].ordem
    newEstados[index - 1].ordem = temp
    
    const updates = [
      { id: newEstados[index].id, ordem: newEstados[index].ordem },
      { id: newEstados[index - 1].id, ordem: newEstados[index - 1].ordem }
    ]
    
    reorderMutation.mutate(updates)
  }

  const handleMoveDown = (index: number) => {
    if (index === estados.length - 1) return
    
    const newEstados = [...estados]
    const temp = newEstados[index].ordem
    newEstados[index].ordem = newEstados[index + 1].ordem
    newEstados[index + 1].ordem = temp
    
    const updates = [
      { id: newEstados[index].id, ordem: newEstados[index].ordem },
      { id: newEstados[index + 1].id, ordem: newEstados[index + 1].ordem }
    ]
    
    reorderMutation.mutate(updates)
  }

  const predefinedColors = [
    { name: 'Azul', value: '#3B82F6' },
    { name: 'Verde', value: '#10B981' },
    { name: 'Amarelo', value: '#F59E0B' },
    { name: 'Vermelho', value: '#EF4444' },
    { name: 'Roxo', value: '#8B5CF6' },
    { name: 'Rosa', value: '#EC4899' },
    { name: 'Índigo', value: '#6366F1' },
    { name: 'Cinza', value: '#6B7280' }
  ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Estados de Ordens de Serviço</h1>
          <p className="text-gray-600 mt-1">
            Configure os estados disponíveis para as ordens de serviço
          </p>
        </div>
        <Button
          onClick={() => handleOpenModal()}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          Novo Estado
        </Button>
      </div>

      {/* Lista de estados */}
      <div className="bg-white rounded-lg shadow">
        {estados.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">Nenhum estado cadastrado</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {estados
              .sort((a, b) => a.ordem - b.ordem)
              .map((estado, index) => (
                <div
                  key={estado.id}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {/* Cor */}
                      <div
                        className="w-8 h-8 rounded-full border-2 border-gray-300"
                        style={{ backgroundColor: estado.cor }}
                      />
                      
                      {/* Informações */}
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="text-sm font-medium text-gray-900">
                            {estado.nome}
                          </h3>
                          {!estado.ativo && (
                            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                              Inativo
                            </span>
                          )}
                          {estado.estadoInicial && (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                              Inicial
                            </span>
                          )}
                          {estado.estadoFinal && (
                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                              Final
                            </span>
                          )}
                        </div>
                        {estado.descricao && (
                          <p className="text-sm text-gray-500 mt-1">
                            {estado.descricao}
                          </p>
                        )}
                        <div className="flex items-center space-x-4 text-xs text-gray-400 mt-1">
                          {estado.notificarCliente && (
                            <span>Notifica cliente</span>
                          )}
                          {estado.notificarTecnico && (
                            <span>Notifica técnico</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Ações */}
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === estados.length - 1}
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenModal(estado)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      {estado.podeExcluir && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowDeleteConfirm(estado.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Modal de criação/edição */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingEstado ? 'Editar Estado' : 'Novo Estado'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome *
              </label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: Em Análise"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ordem
              </label>
              <Input
                type="number"
                value={formData.ordem}
                onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) })}
                min={0}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <TextArea
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              placeholder="Descrição do estado"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cor
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={formData.cor}
                onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                className="h-10 w-20"
              />
              <div className="flex space-x-1">
                {predefinedColors.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, cor: color.value })}
                    className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-400"
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.ativo}
                onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Ativo</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.estadoInicial}
                onChange={(e) => setFormData({ ...formData, estadoInicial: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Estado inicial (novo OS)</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.estadoFinal}
                onChange={(e) => setFormData({ ...formData, estadoFinal: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Estado final (conclusão)</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.notificarCliente}
                onChange={(e) => setFormData({ ...formData, notificarCliente: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Notificar cliente ao mudar para este estado</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.notificarTecnico}
                onChange={(e) => setFormData({ ...formData, notificarTecnico: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Notificar técnico ao mudar para este estado</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.podeEditar}
                onChange={(e) => setFormData({ ...formData, podeEditar: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Permite edição</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.podeExcluir}
                onChange={(e) => setFormData({ ...formData, podeExcluir: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Permite exclusão</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseModal}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {editingEstado ? 'Atualizar' : 'Criar'} Estado
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal de confirmação de exclusão */}
      <Modal
        isOpen={showDeleteConfirm !== null}
        onClose={() => setShowDeleteConfirm(null)}
        title="Confirmar Exclusão"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Tem certeza que deseja excluir este estado?
          </p>
          <p className="text-sm text-red-600">
            Esta ação não pode ser desfeita e pode afetar ordens de serviço existentes.
          </p>
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(null)}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (showDeleteConfirm !== null) {
                  deleteMutation.mutate(showDeleteConfirm)
                }
              }}
              isLoading={deleteMutation.isPending}
            >
              Excluir
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default EstadosOSPage